import { EventEmitter, Injectable } from "@angular/core";
import { Product } from "./shop.product.model";
import { Donation } from "../donation/donation";
import { ShopCartItem } from "./shop.cart.item";
import { TrackingComponent } from "../tracking/tracking.component";
import { ShopProductService } from "./shop.product.service";
import { PRODUCT_ID_DIGITAL_DOWNLOAD, PRODUCT_ID_OPTIONAL_DONATION } from "./shop.config";

/**
 * Service that is holding the current content of the cart in the shop.
 */
@Injectable({
  providedIn: 'root',
})
export class ShopCartService {
  /**
   * Eventimitter if something changed in the selected products
   */
  public itemsUpdated: EventEmitter<void> = new EventEmitter();

  /**
   * Holds the donation object where the order is saved.
   */
  donation: Donation;

  /**
   * list of available categories
   */
  categories: { id: number, name: string, product_count: number }[] = [];

  /**
   * Items in the chart
   */
  items: ShopCartItem[] = [];

  /**
   * overall count of items. If you have two products and each two times the overall count will be four
   */
  overallCount = 0;

  /**
   * The overall price of all items in the chart
   */
  overallPrice = 0;

  /**
   * The overall price of all items in the chart without the extra factor
   */
  itemsPrice = 0;

  /**
   * extra factor far calculating the overall sum.
   */
  extraFactor = 1;

  /**
   * gift count
   */
  giftCount = 0;

  /**
   * overall price without the custom products for additional donations (e.g. optional donation)
   */
  overallPriceWithoutCustomProducts = 0;

  /**
   * custom product ids
   */
  customProductIds = [PRODUCT_ID_DIGITAL_DOWNLOAD, PRODUCT_ID_OPTIONAL_DONATION];

  /**
   * constructor
   * @param shopProductService 
   */
  constructor(private shopProductService: ShopProductService) {
  }

  /**
   * Sets the donation object that should be used for saving the products.
   *
   * @param donation
   */
  setDonation(donation: Donation): void {
    this.donation = donation;
  }

  /**
   * set the available categories.
   * @param categories
   */
  setCategories(categories: { id: number, name: string, product_count: number }[]): void {
    this.categories = categories;
  }

  /**
   * Returns all the items in the cart
   */
  getItems(): ShopCartItem[] {
    return this.items;
  }

  /**
   * Get the number of items. (each product one time)
   */
  getItemCount(): number {
    return this.items.length;
  }

  /**
   * get the overall count, sum of product * count
   */
  getOverallCount(): number {
    return this.overallCount;
  }

  /**
   * Get the price of all items in the cart without the extra factor
   */
  getItemsPrice(): number {
    return this.itemsPrice;
  }

  /**
   * Get the overall price of all products/items in the cart
   */
  getPrice(): number {
    return this.overallPrice;
  }

  /**
   * Get the overall price of all products/items in the cart without the custom products
   */
  getPriceWithoutCustomProducts(): number {
    return this.overallPriceWithoutCustomProducts;
  }

  /**
   * set an extra fctor for the shopping cart
   * @param extraFactor
   */
  setExtraFactor(extraFactor: number): void {
    this.extraFactor = extraFactor;
    this.recalculate();
  }

  /**
   * returns the extra factor for the shopping cart
   */
  getExtraFactor(): number {
    return this.extraFactor;
  }

  /**
   * set the gift count
   */
  setGiftCount(giftCount: number): void {
    this.giftCount = giftCount;
  }

  /**
   * Get the gift count
   */
  getGiftCount(): number {
    return this.giftCount;
  }

  /**
   * Set an item to the cart or updates the count of a product in the cart
   * @param product - the product that should be added
   * @param count - count of that product
   * @param increaseCountIfProductsExists - if the product is already in the cart, increase the count, instead of adding a new item
   * @param freePrice - price if a free price is available
   * @param text - text if a text is available
   * @param forceCountModifiable - if the count should be modifiable in the cart
   */
  addItem(product: Product, count: number, increaseCountIfProductsExists: boolean = true, freePrice: number = null, text: string = null, forceCountModifiable: boolean = false): void {
    const uuid = ShopCartItem.generateUUID(product, freePrice, text);
    const productInChart = this.items.find(element => element.uuid === uuid);

    // make sure that the price has only two decimals
    if (freePrice % 1 !== 0) {
      freePrice = parseFloat(freePrice.toFixed(2));
    }

    if (increaseCountIfProductsExists && productInChart) {
      // update itemcount
      productInChart.count += count;
    } else {
      // add item as new element
      this.items.push(new ShopCartItem(product, count, this.findCategoryNames(product), freePrice, text, forceCountModifiable));
    }

    // sent event
    TrackingComponent.sentMessage(TrackingComponent.MESSAGE_SHOP_ADDTOCART, {
      product: product.name,
      internal_id: product.internal_id,
      count: count,
      price: freePrice || product.price
    });

    this.recalculate();
  }

  /**
   * Finds all categorynames for a product
   * @param product
   * @public
   */
  public findCategoryNames(product: Product): string[] {
    const categories = [];
    this.categories.map((element) => {
      if (product.categories.indexOf(element.id) !== -1) {
        categories.push(element.name);
      }
    });
    return categories;
  }

  private recalculate() {
    let hasDecimals = false;

    // reset all values
    this.overallCount = 0;
    this.overallPrice = 0;
    this.overallPriceWithoutCustomProducts = 0;
    this.itemsPrice = 0;

    // iterate over all items
    this.items.forEach(item => {
      const { product, count, freePrice } = item;
      const price = freePrice ?? product.price;
      const isCustomProduct = this.customProductIds.includes(product.id);

      // update overall count
      this.overallCount += count;

      // calculate prices
      const basePrice = price * count;
      const priceWithExtraFactor = price * count * this.extraFactor;

      if (isCustomProduct) {
        // if custom product, add the base price to the overall price without extra factor
        this.overallPrice += basePrice;
      } else {
        // if no custom product, add the price with extra factor
        this.overallPrice += priceWithExtraFactor;
        this.overallPriceWithoutCustomProducts += priceWithExtraFactor;
      }

      // always add to itemsPrice
      this.itemsPrice += basePrice;

      // Check for decimals
      if (price % 1 !== 0) {
          hasDecimals = true;
      }
    });

    // Save the products of the cart to the donation
    this.updateProductsInDonation();

    // Inform all elements that something has changed
    this.itemsUpdated.emit();

    // Update decimal status in the cart
    this.shopProductService.setDecimalsShownCart(hasDecimals);
  }

  /**
   * Updates the product data in the donation object.
   */
  public updateProductsInDonation(): void {
    if (this.donation instanceof Donation) {
      this.donation.products = [];
      this.items.forEach(item => {
        this.donation.products.push({
          id: item.product.id,
          product: item.product.name,
          productPlural: item.product.name_plural,
          productInternalId: item.product.internal_id,
          count: item.count * this.extraFactor,
          price: item.freePrice !== null ? item.freePrice : item.product.price,
          categories: item.categories,
          productText: item.product.text,
          giftSender: item.giftSender,
          giftReceiver: item.giftReceiver,
          giftMessage: item.giftMessage,
          text: item.text
        });
      });
    }
  }

  /**
   * sets an item to a defined count
   * @param product
   * @param count
   * @param uuid
   */
  setItemCount(product: Product, count, uuid = null): boolean {
    let productInChart = this.items.find(element => element.product.id === product.id);

    if (uuid) {
      // if uuid is set, search for the item with the uuid
      productInChart = this.items.find(element => element.uuid === uuid);
    }

    if (productInChart) {
      productInChart.count = count;
      this.recalculate();
      return true;
    }
    return false;
  }

  /**
   * sets an item to a defined count
   * @param productIndex
   * @param count
   */
  setItemCountByIndex(productIndex: number, count): boolean {
    if (this.items[productIndex]) {
      this.items[productIndex].count = count;
      this.recalculate();
      return true;
    }
    return false;
  }

  /**
   * Removes an item from the cart
   * @param product
   */
  removeItem(product: Product, uuid = null): boolean {
    let productIndex = this.items.findIndex(element => element.product.id === product.id);

    if (uuid) {
      // if uuid is set, search for the item with the uuid
      productIndex = this.items.findIndex(element => element.uuid === uuid);
    }

    if (productIndex > -1) {
      this.items.splice(productIndex, 1);
      this.recalculate();
      return true;
    }
    return false;
  }

  /**
   * Removes an item from the cart
   * @param productIndex
   */
  removeItemByIndex(productIndex: number): boolean {
    if (productIndex > -1) {
      this.items.splice(productIndex, 1);
      this.recalculate();
      return true;
    }
    return false;
  }
}
